import format from "date-fns/format";
import { Frames } from "frames-react";
import enGb from 'date-fns/locale/en-GB';
import React, { useContext, useEffect, useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import isABTest from "../../ab/isabtest";
import API from "../../api/phx";
import { getDomainConfig } from "../../config/config";
import { ABContext } from "../../contexts/ab-context";
import { DepDateContext } from "../../contexts/dep-date-context";
import { OrderContext } from "../../contexts/order-context";
import OrderSummaryContext from "../../contexts/order-summary-context";
import { useCart } from "../../hooks/useCart";
import { filterNullValues, getCartEarliestDate, getDate } from "../../utils/cart.js";
import { getCurrency } from "../../utils/getCurrency";
import { getLanguageCode } from "../../utils/getURLParams";
import BillingAddress from "../BillingAddress/BillingAddress";
import CartSummary from "../CartSummary/CartSummary";
import CuxInfo from "../CuxInfo/CuxInfo";
import DepartureDate from "../DepartureDate/DepartureDate";
import DiscountForm from "../DiscountForm/DiscountForm";
import { applyGiftCards } from "../GiftCard/applyGiftCards";
import ExternalPaymentsOrderProgressModal from '../ExternalPaymentsOrderProgressModal/ExternalPaymentsOrderProgressModal';
import Marketing from "../Marketing/Marketing";
import PaymentDetails from "../PaymentDetails/PaymentDetails";
import PaymentOptions from "../PaymentOptions/PaymentOptions";
import SubTotal from "../SubTotal/SubTotal";
import Cro1381SubTotal from "../SubTotal/Cro1381Subtotal";
import Terms from "../Terms/Terms";
import TicketDetails from "../TicketDetails/TicketDetails";
import StandardButton from "../UI/StandardButton.js";
import { applePay } from "../ApplePay/applePay";
import { countryCode } from "../../utils/countryCode";
import Cookies from 'js-cookie';
import { googleDataLayer } from '../Analytics/Universal/PageView/googleDataLayer'
import { ReactComponent as LockIcon } from '../../images/global/lock-fill.svg';
import {ReactComponent as BasketIcon} from '../../images/global/basket-logo.svg';
import {ReactComponent as LiveChatIcon} from '../../images/global/live-chat-icon.svg';
import {ReactComponent as SpeechIcon} from '../../images/global/speech.svg';
import { PaypalContext } from "../Paypal/PaypalV3.js";
import { useSettings } from "../../hooks/useSettings";
import { Logs } from "../../utils/logs";
import Login from "../Analytics/Universal/PageView/Login.js";
import { PhoneNumberFormatter } from "../../utils/phoneNumberFormatter.js";
import { Gpay, googlePay, processPaymentData } from '../GooglePay/googlePay.js';
import { markOrderPaymentStatusFailed } from '../../utils/markPaymentAsFailed';
import filterData from "../../utils/filters/requestData";
import StandardH4 from "../UI/StandardH4";
import TrustBox from "../TrustPilot/TrustPilot";
import { FeatureRelease } from '../../utils/controlledRelease';
import SocialLove from '../SocialLove/SocialLove';
import ExitPopUp from '../../utils/exitPopUp';
import { datadogRum } from "@datadog/browser-rum";

const Form = () => {

  const config = getDomainConfig();
  const { t } = useTranslation('common');
  const { isSuccess: cartIsSuccess, data: cartContents } = useCart();
  /* LOADING THE BRANDED SCSS */
  const [styles, setStyles] = useState(false);

  let currency;

  // Start the auto log
  Logs.StartAutoSender()

  useEffect(() => {
    const getStyles = async (theFileName, theFileEnd) => {
      if (config.brand) {
        let moduleStyles = false;
        try {
          moduleStyles = await import(
            "./" + theFileName + config.brand + theFileEnd
          );
          setStyles(moduleStyles);
        } catch (e) {
          console.error(
            `Styles for ${theFileName} could not be loaded, ${e.message}`
          );
        }
      }
    };

    // Specify file name and end of file name here.
    getStyles("Form", ".module.scss");
  }, [config.brand]);

  /* END LOADING THE BRANDED SCSS */
  const [abTests, setABTests] = useState({});
  const [order, setOrder] = useState({});
  const [showPaymentError, setShowPaymentError] = useState(false);
  const [showDeliveryAddressOption, setShowDeliveryAddressOption] = useState(false);
  const [showDiscountForm, setShowDiscountForm] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [paymentIsValid, setPaymentIsValid] = useState(false);
  const [paymentError, setPaymentError] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [isGiftCardUsed, setIsGiftCardUsed] = useState(false);
  const [isPostagePaid,setIsPostagePaid] = useState(false);
  const [showExternalPaymentError, setShowExternalPaymentError] = useState(false)
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isFive9Available, setIsFive9Available] = useState(false);
  const [cancellationMessage, setCancellationMessage] = useState('');

  // 3DS messaging
  const [orderCreationStatus, setOrderCreationStatus] = useState("");
  const [message3DSShowModal, setMessage3DSShowModal] = useState(false);

  // TODO remove when all brands will be moved to CKO V3
  const { isSuccess: settingsIsSuccess, data: settingsData } = useSettings();

  // we need to check if the chat is active
  // the module is loaded async and we have no control on that
  let five9Retry = 10;
  let five9Interval = setInterval(() => {
    if (window.Five9SocialWidget) {
      if(window.Five9SocialWidget.widgetAdded) {
        setIsFive9Available(true);
        clearInterval(five9Interval);

        const five9ElementClose = document.getElementById('five9-minimize-icon');

        if (typeof(five9ElementClose) != 'undefined' && five9ElementClose != null) {
            five9ElementClose.onclick = () => {
            setIsChatOpen(false);
          }
        }

        return;
        }
    }

    if (five9Retry === 0) {
      clearInterval(five9Interval);
      return;
    }
    five9Retry++;
  }, 300);

  let CKOversion = 0;
  if (settingsIsSuccess) {
    CKOversion = +settingsData.version
  }

  const abContext = useContext(ABContext);

  // CRO-1381-checkout-redesign AB test
  const [isCro1381, setIsCro1381] = useState(false);

  // Incremental cookie fix - check for cookie
  function getCookieValue(name) {
    let matches = document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)');
    return matches ? matches.pop() : '';
  }

  useEffect(() => {
    if (config.brand !== 'Sw') {
      // Determine if it's an A/B test variant
      //const cro1381IsVariant = isABTest("CRO-1381-checkout-redesign");
      const featureRelease = new FeatureRelease("CRO-1381-checkout-redesign", { 0: 0, 1: 100 });
      const cro1381IsVariant = featureRelease.Get() === '1';

      const testCro1381 = {
        cro1381: {
          name: 'CRO-1381-checkout-redesign',
          variant: cro1381IsVariant,
        },
      };

      // Update the A/B tests state
      setABTests((prevState) => ({ ...prevState, ...testCro1381 }));

      // Check if A/B test variant and update the state
      if (testCro1381.cro1381 !== undefined) {
        setIsCro1381(testCro1381.cro1381.variant);
      }

      // Remove this when we reinstate isABTest
      if (cro1381IsVariant) {
        document.body.classList.add('CRO-1381-checkout-redesign');
      } else {
        document.body.classList.remove('CRO-1381-checkout-redesign');
      }
      // Remove this when we reinstate isABTest

    }

  }, [config.brand]);
  // END CRO-1381-checkout-redesign AB test

  // CHEC-642 Hiding deposits
  const [isChec642, setIsChec642] = useState(false);

  useEffect(() => {
    if (getLanguageCode(config.brand,true) === "/en" || getLanguageCode(config.brand,true) === "/en-ie") {
      const chec642IsVariant = isABTest("CHEC-642-hiding-deposits");
      const testChec642 = {
        chec642: {
          name: "CHEC-642-hiding-deposits",
          variant: chec642IsVariant,
        },
      };

      setABTests((prevState) => ({ ...prevState, ...testChec642 }));

      if (testChec642.chec642 !== undefined) {
        setIsChec642(testChec642.chec642.variant);
      }

    }
  }, [config.brand]);
  // CHEC-642 Hiding deposits END

  // For departure date
  const depDateContext = useContext(DepDateContext);

  // Reused from PaymentOptions.js. Will implement a better approach is ABtest wins. Determines whether payment options should be displayed to the user.
  const shouldShowPaymentOptions = () => {
    if (!cartIsSuccess) return false;

    const depositDays = cartMeta.payment_options?.days;

    const cutoffDate = new Date(Date.parse(depDateValue.depDate));

    if (depositDays) {
      cutoffDate.setDate(cutoffDate.getDate() - parseInt(depositDays));

      if ((+cartMeta.payment_options.deposit_enabled + +cartMeta.payment_options.qualify) < 2) {
        return false;
      }

      if (cutoffDate < new Date()) {
        return false;
      }
    }

    let totalPrice = cartMeta.total_price;
    if (cartMeta.total_discount) {
      totalPrice = totalPrice - parseFloat(cartMeta.total_discount).toFixed(2);
    }

    if (+cartMeta?.total_hotel_tax > 0) {
      totalPrice = +totalPrice + +cartMeta.total_hotel_tax;
    }

    if (orderSummary.deliveryCharges) {
      totalPrice = parseFloat(Number(totalPrice) + Number(orderSummary.deliveryCharges.price)).toFixed(2);
    }

    if (cartMeta.total_gift_card_balance > 0) {
      totalPrice = parseFloat(Number(totalPrice) - Number(cartMeta.total_gift_card_balance)).toFixed(2);
    }

    if (totalPrice < 0) {
      return false;
    }

    return true;
  };
  // END

  // Success and failure handlers for payments.
  const handlePaymentSuccess = (orderId, orderSid) => {

    if (cartContents.meta.total_gift_card_balance > 0) {
      applyGiftCards(
        orderId,
        cartContents.data.gift_cards,
        cartContents.meta.total_price
      )
    }

    // Clearing current cart
    config.brand === 'Atd' && API.delete('/cart');

    // Cookies below are assigned if payment was not flagged or risked. Is for Global order complete page to display correct information for the user.
    Cookies.set('orderDoComplete',true);
    Cookies.set('risk',false);
    Cookies.set('flagged',false);
    Cookies.set('display',true);

    ExitPopUp.Disable();

    // Redirect:
    window.location.href = `${getLanguageCode(config.brand,true)}/cart/checkout/complete/${orderId}`;
  };

  const handlePaymentFail = (orderId) => {
    // Clearing current cart
    let completePath = 'complete';
    if (config.brand === 'Atd') {
      API.delete('/cart');
      completePath = 'incomplete';
    }

    // Cookies below are assigned if payment was failed, but not flagged or risked. Is for Global order complete page to display correct information for the user.
    Cookies.set('orderDoComplete',false);
    Cookies.set('risk',false);
    Cookies.set('flagged',false);
    Cookies.set('display',false);

    ExitPopUp.Disable();

    // Redirect to complete page (info displayed there will show to user that his payment was failed based on cookies above):
    window.location.href = `${getLanguageCode(config.brand,true)}/cart/checkout/${completePath}/${orderId}`;
  };

  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const success = urlParams.get('success');
  const fail = urlParams.get('fail');

  useEffect(() => {
    if(cartIsSuccess){
      if (success) {
        handlePaymentSuccess(success);
      }
      if (fail) {
        handlePaymentFail(fail);
      }
    }
  },[success,fail, cartIsSuccess])

  const {
    control,
    register,
    trigger,
    resetField,
    formState: {errors, touchedFields, isValid, isSubmitted},
    handleSubmit,
    setValue,
    getValues,
    clearErrors,
    setError,
  } = useForm({ mode: "onBlur", reValidateMode: "onBlur" });

  const [orderSummary, setOrderSummary] = useState({});
  const orderSummaryValue = { orderSummary, setOrderSummary };
  const [depDate, setDepDate] = useState("")
  const depDateValue = { depDate, setDepDate };
  const [cardIsAccepted, setCardIsAccepted] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [shouldShow3ds, setShouldShow3ds] = useState(false);

  // Functions to allow checks to bubble up from the payment component.
  const paymentIsValidCheck = (response,payment_method, isCardAccepted) => {
    setPaymentMethod(payment_method);
    setPaymentIsValid(response);
    setCardIsAccepted(isCardAccepted);
  };
  const paymentErrorCheck = (response) => {
    setPaymentError(response);
  };

  let earliestDepDate;
  let cartMeta = [];
  let cartData = [];
  if (cartIsSuccess) {
    cartMeta = cartContents.meta;
    cartData = cartContents.data;
    currency = getCurrency(cartContents);

    if(cartMeta.total_price < 1 && config.siteName === 'Universal Bookings'){
      ExitPopUp.Disable();
      const url = new URL(`https://${window.location.hostname}/shopping-cart`);
      window.location.href = url;
    }

    if(+cartMeta.total_price === 0 && config.brand === 'Atd' && !("orderId" in order) && !success && !fail){
      ExitPopUp.Disable();
      window.location.href = new URL(`https://${window.location.hostname}/${getLanguageCode(config.brand)}/cart`);
    }

    earliestDepDate = getCartEarliestDate(cartContents.data.tickets, cartMeta);
    if (earliestDepDate) {
      depDateValue.depDate = earliestDepDate;
    }
  }

  const cartIsFull = useRef(false);

  useEffect(() => {
    if (cartIsSuccess) {
      setShowDeliveryAddressOption(
        !!cartMeta.checkout_rules.show_delivery_address
      );

      if(!cartIsFull.current) {
        cartIsFull.current = true;
        Logs.AddEvent(`Full cart details: ${JSON.stringify(cartContents)}`)
      }

      cartContents.data.tickets.some(ticket => {
        // Product with id 293890 is Postage payment for printed Combo tickets
        if (parseFloat(ticket.product_id) === 293890){
          setIsPostagePaid(true);
        }
        return null;
      })

      // Show discount form?
      if (
        cartMeta.checkout_rules.discounts.enable_discounts &&
        cartMeta.checkout_rules.discounts.show_discount_form
      ) {
        setShowDiscountForm(true);
      }

      // BAU-367 Check if gift card has been applied to order.
      if(cartContents.data.gift_cards.length > 0){
        if(cartContents.data.gift_cards.length > 1){
          const giftCards = cartContents.data.gift_cards
          let giftCardSerials = giftCards.map(giftCard => giftCard.serial).join(', ')
          setIsGiftCardUsed('Gift cards used - Serials: ' + giftCardSerials)
        } else {
          const giftCard = cartContents.data.gift_cards[0].serial
          setIsGiftCardUsed("Gift card used - Serial: " + giftCard)
        }
      } else {
        setIsGiftCardUsed(false)
      }
    }

  }, [cartIsSuccess, cartMeta]);

  // If there's a payment error show all form fields and show error.
  useEffect(() => {
    if (paymentError) {
      setFormSubmitted(false);
      setShowPaymentError(true);
    }
  }, [paymentError]);

  // AB testing

  // CRO-986-Free-cancellation-messaging-on-AA-checkout
  useEffect(() => {
    // Conditionals for test
    let isDisneyTickets = false;
    if (cartContents !== undefined) {
      const ticketsInCart = cartContents.data.tickets;

      ticketsInCart.map((cartItem) => {
        const isDisneyProductIds = [
          "277621",
          "278859",
          "91366",
          "279167",
          "279185",
          "277861",
          "4148",
          "31253",
          "602",
          "279198",
          "137313",
          "279198",
          "200089",
          "282136",
          "284763",
          "287530",
          "215647",
          "279109",
          "284376",
          "289580",
          "284455",
        ];

        if (isDisneyProductIds.includes(cartItem.product_id)) {
          isDisneyTickets = true;
        }
        return null;
      });
    }

    if (config.brand === "Aa" && isDisneyTickets) {
      const cro986IsVariant = isABTest(
        "CRO-982-Free-cancellation-messaging-on-checkout"
      );

      // Add context
      const testCro986 = {
        cro986: {
          name: "CRO-986-Free-cancellation-messaging-on-AA-checkout",
          variant: cro986IsVariant,
        },
      };

      // Update state
      setABTests((prevState) => {
        return { ...prevState, ...testCro986 };
      });
    }
  }, [config.brand, cartContents]);

  // CRO-1259-magicband-v3
  useEffect(() => {
    let isMagicBandTickets = false;
    if (cartContents !== undefined) {
      const ticketsInCart = cartContents.data.tickets;

      ticketsInCart.map((cartItem) => {
        const isMagicBandTicketIds = [
          // "295995", Pausing magic band V3 test. To enable, uncomment these two lines
          // "320292", Pausing magic band V3 test. To enable, uncomment these two lines
        ];
        if (isMagicBandTicketIds.includes(cartItem.ticket_id)) {
          isMagicBandTickets = true;
        }
      });
    }

    if (isMagicBandTickets) {
      const cro1259IsVariant = isABTest(
        "CRO-1259-magic-band-v3"
      );

      // Add context
      const testCro1259 = {
        cro1259: {
          name: "CRO-1259-magic-band-v3",
          variant: cro1259IsVariant,
        },
      };

      // Update state
      setABTests((prevState) => {
        return { ...prevState, ...testCro1259 };
      });
    }
  }, [cartContents]);
  // END CRO-1259-magicband-v3

  // CRO-1202-All-hide-mobile-field
  useEffect(() => {
    if (config.brand === "Uo") {
      const cro1202IsVariant = isABTest(
        "CRO-1202-All-hide-mobile-field"
      );

      // Add context
      const testCro1202 = {
        cro1202: {
          name: "CRO-1202-All-hide-mobile-field",
          variant: cro1202IsVariant,
        },
      };

      // Update state
      setABTests((prevState) => {
        return {...prevState, ...testCro1202};
      });
    }
  }, [config.brand]);

  const country_code = countryCode();

  // CRO-1381-checkout-redesign - Show/hide
  const [showSummary, setShowSummary] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  let showHideHeadingClass = "ticketSummary ticketSummaryHide";
  let showHideTableClass = styles.cro1381HideMobile;

  if (showSummary) {
    showHideHeadingClass = "ticketSummary ticketSummaryOpen";
    showHideTableClass = styles.cro1381ShowMobile;
  }

  useEffect(() => {
    const handleResize = () => {
      const newWidth = window.innerWidth;
      if (Math.abs(windowWidth - newWidth) > 10) {
        setWindowWidth(newWidth);
      }
    };

    const debouncedHandleResize = debounce(handleResize, 250);
    window.addEventListener('resize', debouncedHandleResize);

    return () => window.removeEventListener('resize', debouncedHandleResize);
  }, [windowWidth]);

  useEffect(() => {
    if (windowWidth > 1024) {
      setShowSummary(false);
    }
  }, [windowWidth]);

  // Debounce function to limit the frequency of the re-rendering on change screen size
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
  // CRO-1381-checkout-redesign - Show/hide close

  const onSubmit = async (data) => {

    switch (paymentMethod) {
      case 'card':
        setOrderCreationStatus("creating");
        setMessage3DSShowModal(true);
        if (!paymentIsValid || !cardIsAccepted) {
          setShowPaymentError(true);
          setOrderCreationStatus("");
          setMessage3DSShowModal(false);
          // Don't submit the form.
          return;
        }
        break;

      case 'apple-pay':
        // eslint-disable-next-line no-undef
        var applePaySession = new ApplePaySession(6, {
          currencyCode: cartMeta.sale_cur,
          countryCode: country_code,
          merchantCapabilities: ['supports3DS'],
          supportedNetworks: ['amex', 'masterCard', 'maestro', 'visa'],
          total: {
            label: config.externalPayLabel,
            amount: orderSummary.total,
            type: 'final'
          }
        });
        break;

      case 'gift-card':
        if (+orderSummary.total !== 0) {
          setShowPaymentError(true);
          // Don't submit the form.
          return;
        }
        break;

      default:
        break;
    }

    if (paymentMethod !== 'card') {
      setOpenModal(true);
      setOrderCreationStatus("creating");
    }

    // Clear any error that may have been set.
    setShowPaymentError(false);

    // Hide all form elements bar payments.
    setFormSubmitted(true);

    // Build basic order object.
    let orderDetails = {
      email: data.email,
      departure_date: earliestDepDate
        ? getDate(earliestDepDate, "iso")
        : getDate(data.departure_date, "iso"),
      agree_terms: 1,
      agree_privacy: 1,
      agree_marketing: data.marketing ? 1 : 0,
      source: "Web",
      order_charges: [],
      order_log: [{
        "description": `Payment method selected: ${paymentMethod} ${isGiftCardUsed ? isGiftCardUsed : ""}`,
      }]
    };

    if (orderSummary.paymentOption === "non-refundable-deposit") {
      orderDetails.comments_admin = [{
        "message": `<b style="color: red;">NON REFUNDABLE DEPOSIT WAS SELECTED! AMOUNT: ${orderSummary.total} ${currency}. THIS AMOUNT SHOULDN'T BE REFUNDED!</b>`}];
    }

    // Add delivery charges if needed.
    if (orderSummary.deliveryCharges) {
      orderDetails['order_charges'] = {
        price: orderSummary.deliveryCharges.price,
        title: orderSummary.deliveryCharges.title,
        type: "shipping",
      }
    }
    // combine phone number and country code and format so its compatible with 5/9
    if (isCro1381) {
      const phoneNumberFormatter = new PhoneNumberFormatter()
      const processedNumber = phoneNumberFormatter.formatInternational(data['phone-field-masked'], data['phone-field-country-code'])
      data["billing-address-phone"] = processedNumber;
    }

    // Add Address(es).
    Object.keys(data)
    .filter((key) => key.match(/-address-/))
    .forEach((key) => {
      const newKey = key.replaceAll("-", "_");
      orderDetails[newKey] = data[key];

      // Copy billing over delivery if needed.
      if (data["copy-address"]) {
        orderDetails[newKey.replace("billing_", "delivery_")] = data[key];
      }

    });
    // Add attributes to cart.
    const cartItemAttributeValues = {};
    const guestListAtt = {};

    // Function to capitalize the Adult, Child and Infant values
    function capitalizeFirstLetter(string) {
      return string.charAt(0).toUpperCase() + string.slice(1);
    }

    Object.keys(data)
      .filter((key) => key.match(/^attr_/))
      .forEach((key) => {
        const parts = key.split("_");
        const ticketId = parts[2];
        const cartItemId = parts[1];
        const aid = parts[4];
        let num = parts[5];
        const attributeId = `${ticketId}-0-${aid}`; // Zero because there's no order_product_id until there's an order.
        if (!(cartItemId in cartItemAttributeValues)) {
          cartItemAttributeValues[cartItemId] = {
            cart_item_id: cartItemId,
            attributes: {},
          };
        }
        if (!(attributeId in cartItemAttributeValues[cartItemId].attributes)) {
          cartItemAttributeValues[cartItemId].attributes[attributeId] = {
            attribute_id: attributeId,
            values: [],
          };
        }


        // aid 16 stands for Date attribute which comes as required field for Eurostar tickets, Date object has to be
        // converted to date format picked up by D6 and send it to Eurostar to book a ticket
        if (aid === '16') {
          cartItemAttributeValues[cartItemId].attributes[attributeId].values[
            num
          ] = format(new Date(data[key]),'dd MMM yyyy',{locale: enGb})
        } else {
          cartItemAttributeValues[cartItemId].attributes[attributeId].values[
            num
          ] = data[key];
        }

        // if attribute id is 50 we got the guestlist not the lead passenger
        // The we assign the values to the guestlistAtt object
        if (aid === "50") {
          const guesttype = parts[7];
          const guestField = parts[6];
          const guestNum = parts[9];
          const guestLastNameFieldNum = parts.length === 11 ? +parts[10] : 0;

          if (!(cartItemId in guestListAtt)) {
            guestListAtt[cartItemId] = {};
          }

          if (!(guesttype in guestListAtt[cartItemId])) {
            guestListAtt[cartItemId][guesttype] = {};
          }

          if (!(guestNum in guestListAtt[cartItemId][guesttype])) {
            guestListAtt[cartItemId][guesttype][guestNum] = {};
          }

          if (!(guestField in guestListAtt[cartItemId][guesttype][guestNum])) {
            guestListAtt[cartItemId][guesttype][guestNum][guestField] = data[key];
          }

          if (guestLastNameFieldNum > 0) {
              guestListAtt[cartItemId][guesttype][guestNum]['last_name'] = data[key];
          }

          // looping through the guestlistAtt object and assigning the values to the cartItemAttributeValues object
          Object.keys(guestListAtt).forEach((keys) => {
            let adultGuestString = "";
            let childGuestString = "";
            let infantGuestString = "";

            Object.keys(guestListAtt[keys]).forEach((key2) => {
              Object.keys(guestListAtt[keys][key2]).forEach((key3) => {
                const addedNum = (+key3 + 1).toString();
                const name = `${guestListAtt[keys][key2][key3].name} ${guestListAtt[keys][key2][key3].last_name}`;
                const title = guestListAtt[keys][key2][key3].title;
                const dob = guestListAtt[keys][key2][key3].date;
                const type = capitalizeFirstLetter(key2);
                let stringDate=''
                if (dob !== undefined) stringDate = format(new Date(dob),'dd MMM yyyy',{locale: enGb}).replace(/ /g, " ");
                const formattedDate = `(${stringDate})`;
                // if the number of type of guest's index is 1 we are adding the type to it , otherwise not
                if (addedNum === "1") {
                  if (type === "Adult") {
                    adultGuestString += `${type}s\n${addedNum}. ${title} ${name}\n`;
                  } else if (type === "Child") {
                    childGuestString += `\n${type}ren\n${addedNum}. ${title} ${name} ${formattedDate}\n`;
                  } else if (type === "Infant") {
                    infantGuestString += `\n${type}s\n${addedNum}. ${title} ${name} ${formattedDate}\n`;
                  }
                } else {
                  if (type === "Adult") {
                    adultGuestString += `${addedNum}. ${title} ${name}\n`;
                  } else if (type === "Child") {
                    childGuestString += `${addedNum}. ${title} ${name} ${formattedDate}\n`;
                  } else if (type === "Infant") {
                    infantGuestString += `${addedNum}. ${title} ${name} ${formattedDate}\n`;
                  }
                }
              });
            });
            // we are assigning the guestlistAtt object to the cartItemAttributeValues object
            const guestStringData =
              adultGuestString + childGuestString + infantGuestString;
            if (cartItemAttributeValues[keys].attributes[attributeId]) {
              // Make sure attribute exists before we assign the value.
              cartItemAttributeValues[keys].attributes[attributeId].values[0] =
                guestStringData;
            }
          });
        }
      });

    // Remove cart item ids and attribute ids from structure:
    const requestBody = Object.keys(cartItemAttributeValues).map((key) => {
      const cartItem = cartItemAttributeValues[key];
      cartItem.attributes = Object.keys(cartItem.attributes).map(
        (key2) => cartItem.attributes[key2]
      );
      return cartItem;
    });

    if (paymentMethod === "google-pay") {
      var googlePaySuccess = false;
      var googlePayError;
      var googlePayClient = Gpay.GetGoogleClient();
      try {
        var paymentData = await googlePay(
          +orderSummary.total,
          cartMeta.sale_cur,
          country_code,
          googlePayClient
        );
        googlePaySuccess = true;
      } catch (err) {
        googlePaySuccess = false;
        googlePayError = err;
      }
    }

    try {
      filterNullValues(requestBody).map((d) => {
        Logs.AddProvisionalEvent('Attributes ' + filterData.filter(JSON.stringify(d)));
      });
    } catch(et) {
      // silent fail
    }

    // reset the order id
    Logs.SetOrderId(-1);
    Logs.AddProvisionalEvent('Attributes API - START');
    API.put(`/cart/attributes`, filterNullValues(requestBody), { withCredentials: true }).then(
      (response) => {
        Logs.AddProvisionalEvent('Attributes API - DONE');

        Logs.AddProvisionalEvent('CreateOrder API - START');
        API.post(`/cart/create_order`, orderDetails, { withCredentials: true }).then(
          (orderResponse) => {
            Logs.AddProvisionalEvent('CreateOrder API - DONE');

            const orderId = orderResponse.data.order_id;
            datadogRum.setUser({
              order_id: orderId.toString(),
            });

            Logs.AddEvent(`Order Created: ${orderId}`);
            Logs.SetOrderId(orderId);
            Logs.SendEvents();
            if (paymentMethod === 'apple-pay') {
              applePaySession.begin()
              applePay(orderDetails, orderId, +orderSummary.total ,cartMeta.sale_cur, country_code, applePaySession, showFormFunc, applePayFailed, showPayWithExternalPaymentMethodFunc, setOrder, setOrderCreationStatus, setOpenModal)
            }

            if (paymentMethod === 'paypal') {
              const order_details = {
                amount: +orderSummary.total,
                currency: cartMeta.sale_cur,
                orderId: orderId,
              };

              PaypalContext.SetOrderDetails(order_details);
            }

            setOrderCreationStatus("reviewing");

            // Log the user in.
            const loginPost = `username=${encodeURIComponent(
              data.email
            )}&order_id=${orderId}`;
            const loginConfig = {
              headers: {
                "Content-Type": "application/x-www-form-urlencoded",
              },
            };

            Logs.AddProvisionalEvent('Session API - START');

            API.post(`/session?no_auth=1`, loginPost, loginConfig).then(
              (loginResponse) => {
                Logs.AddProvisionalEvent('Session API - DONE');
                // Once we have everything we need set the order object.
                setOrder({
                  orderId: orderId,
                  order: orderDetails,
                  sid: loginResponse.data.id,
                  paymentMethod: paymentMethod,
                });
                window.localStorage.setItem("sid", loginResponse.data.id);

                if(paymentMethod === 'google-pay') {
                  if (!googlePaySuccess && googlePayError.statusCode !== '') {
                    showFormFunc();
                    showPayWithExternalPaymentMethodFunc(false);
                    markOrderPaymentStatusFailed(orderResponse.data.order_id,true,'Google Pay');
                    setOrder({});
                    setOpenModal(false);
                    setOrderCreationStatus("");
                    return;
                  } else {
                    processPaymentData(paymentData, orderDetails, +orderSummary.total, cartMeta.sale_cur, country_code, orderResponse.data.order_id, showFormFunc, showPayWithExternalPaymentMethodFunc, setOpenModal, setOrder, setOrderCreationStatus, setShouldShow3ds)
                    setShowExternalPaymentError(true)
                    return;
                  }
                }

                // BAU-326 Data layer event when the order is created.
                if (orderResponse.data.order_id && loginResponse.data.user_id) {
                  googleDataLayer.createOrderCreationEvent(orderId, "in_checkout", loginResponse.data.user_id, paymentMethod);
                }
                Login(loginResponse.data.email, orderId, loginResponse.data.user_id, loginResponse.data.id, loginResponse.data.username, 'automatic through checkout');
              },
              (error) => {
                Logs.AddProvisionalEvent('Session API - ERROR');
                // Couldn't create a session.
                setShowPaymentError(true);
                setFormSubmitted(false);
                setOrderCreationStatus("");
                setMessage3DSShowModal(false);
                console.error(error);
                sendError(error);
              }
            );
          },
          (error) => {
            Logs.AddProvisionalEvent('CreateOrder API - ERROR');
            setFormSubmitted(false);
            console.error(error);
            setOrderCreationStatus("callback");
            sendError(error);
          }
        );
      },
      (error) => {
        Logs.AddProvisionalEvent('Attributes API - ERROR');
        setFormSubmitted(false);
        console.error(error);
        setOrderCreationStatus("callback");
        sendError(error);
      }
    ).catch(err => {
      console.warn(err)
    });
  };

  const [country,setCountry] = useState("");
  const getCountry = (country) => {
    setCountry(country)
  }

  const showFormFunc = () => {
    setFormSubmitted(false);
    Frames.enableSubmitForm();
  }

  const [applePaymentFailed,setApplePaymentFailed] = useState(false);
  const [showExternalPaymentPayMessage, setShowExternalPaymentPayMessage] = useState(false);
  const externalPaymentPayMessageRef = useRef(null);
  const paymentDetailsTitleRef = useRef(null)

  const applePayFailed = () => {
    setApplePaymentFailed(true)
    setShowExternalPaymentError(true)
  };

  const showPayWithExternalPaymentMethodFunc = (state) => {
    setShowExternalPaymentPayMessage(state)
  }

  useEffect(() => {
    // Scroll external payment failed message into view
  if (externalPaymentPayMessageRef.current !== null) {
    if (paymentDetailsTitleRef.current !== null) {
      paymentDetailsTitleRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }
  }, [externalPaymentPayMessageRef.current, showExternalPaymentError])

  useEffect(() => {

    if (cartMeta.sale_cur && settingsData?.paypal) {

      const paypalSettings = settingsData.paypal

      // Adding PayPal script here as its need currency to be passed in to sdk script
      const paypalScript = document.createElement("script");
      paypalScript.setAttribute('nonce', '');
      paypalScript.setAttribute('src', `https://www.paypal.com/sdk/js?client-id=${paypalSettings.client_id}&merchant-id=${paypalSettings.merchant_id}&disable-funding=credit,card,bancontact,blik,eps,giropay,ideal,mercadopago,mybank,p24,sofort,venmo,sepa&commit=true&currency=${cartMeta.sale_cur}`);
      paypalScript.setAttribute('data-partner-attribution-id','CheckoutLtd_PSP');
      paypalScript.setAttribute('data-uid','uid_pgyokqkvlnlxdvxwmfvfrkclpnxtjk');
      document.head.appendChild(paypalScript);
    }

  },[cartIsSuccess, CKOversion])

  const shouldLoadPaypal = (paymentMethod === "paypal") ? true : false

  // This is to scroll the sidebar down (if there's lots of content) when a user hit's the bottom of the page
  // Scroller
  const mainRef = useRef(null);
  const sideRef = useRef(null);

  useEffect(() => {

    if (!isCro1381) {
      // Don't set up the event listener if isCro1381 is not active
      return;
    }

    const handleScroll = () => {
      const main = mainRef.current;
      const side = sideRef.current;
      if (!main || !side) {
        return;
      }

      const mainBottom = main.offsetTop + main.offsetHeight;
      const scrollBottom = window.scrollY + window.innerHeight;

      if (scrollBottom >= mainBottom) {
        side.scrollTo({
          top: side.scrollHeight,
          behavior: 'smooth'
        });

        window.removeEventListener("scroll", handleScroll);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };

  }, [isCro1381]);
  // Scroller END

  const handleClose3DSModal = (e) => {
    e.preventDefault();
    setMessage3DSShowModal(false);
  };

  // Checkbox functionality for main guest
  const [guestInfo, setGuestInfo] = useState({
    firstName: '',
    lastName: '',
    isMainGuestChecked: false
  });

  const handleGuestInfoChange = (info) => {
    setGuestInfo(prevState => ({
      ...prevState,
      ...info
    }));
  };

  const isBlackFridayEnabled = () => {
    const currentLang = getLanguageCode(config.brand, true);
    if(currentLang !== '/en' && currentLang !== "/en-ie") {
      return false;
    }

    if(!!cartMeta.is_hotel_cart) {
      return false;
    }

    if(cartData.tickets) {
      let foundOrlando = false;

      cartData.tickets.map((item) => {
        if (item.destination_title == "Orlando") {
          foundOrlando = true;
        }
      })

      if (!foundOrlando) {
        return false
      }
    }

    if (document.cookie.includes("show-bf=1")) {
      return true;
    }

    const bfStart = new Date('Nov 7, 2024 09:00:00 GMT').getTime();
    const bfEnd = new Date('Dec 2, 2024 23:59:59 GMT').getTime();
    const currentTimestamp = new Date().getTime();
    return currentTimestamp >= bfStart && currentTimestamp <= bfEnd;
  }

  const [isHotelCart, setIsHotelCart] = useState(false);
  // Black Friday
  const [isBlackFridayPeriod, setIsBlackFridayPeriod] = useState(false);
  useEffect(() => {
    if(cartIsSuccess) {
      const isWithinDateRange = isBlackFridayEnabled()
      setIsBlackFridayPeriod(isWithinDateRange);
      if (isWithinDateRange) {
        document.body.classList.add('black-friday-promo');
      } else {
        document.body.classList.remove('black-friday-promo');
      }
    }
    if(cartMeta.is_hotel_cart) {
      setIsHotelCart(true);
    }
  }, [cartIsSuccess]);

  // The new apple pay button is custom element
  // we need to inject the onclick now
  setTimeout(() =>{
    let applePayNative;
    if(applePayNative = document.getElementById('apple-pay-native')) {
      applePayNative.onclick= () => {
        showPayWithExternalPaymentMethodFunc(true);
        googleDataLayer.createUserClickInputEvent('ApplePayButtonClick');
      };
    }
  }, 600);

  return (
    <>
    <ExternalPaymentsOrderProgressModal shouldOpen={openModal} text={orderCreationStatus} show3ds={shouldShow3ds} handleClose3DSModal={handleClose3DSModal}/>
    <div className={styles.container}>
      <ABContext.Provider value={abTests}>
        <div className={styles.page}>
          <div className={styles.top}>
            {!isCro1381 && <h1 className={styles.checkoutH1}>{t('secure-checkout')}</h1>}
            {!isCro1381 && config.brand !== "Sw" && !cartMeta.is_hotel_cart && (
              <>
                <div className={styles.checkoutBackToCart}><a href={getLanguageCode(config.brand,true) + "/cart"}>{t('back-to-basket')}</a></div>
                <div className={styles.checkoutBackToCartSpacer}></div>
              </>
            )}
          </div>
          <div className={styles.main} ref={mainRef}>
            <form
              onSubmit={handleSubmit(onSubmit)}
              className={styles.checkoutForm}
              noValidate
            >
              <OrderContext.Provider value={order}>
                <OrderSummaryContext.Provider value={orderSummaryValue}>
                  <DepDateContext.Provider value={depDateValue}>
                    {(!formSubmitted || config.brand !== "Sw") && (
                      <React.Fragment>
                        {!isCro1381 ?
                          <>
                            {((config.brand !== "Sw" && cartMeta.is_hotel_cart )) ? "" : <CartSummary currency = {currency}/>}
                            {showDiscountForm && <DiscountForm isBlackFridayPeriod={isBlackFridayPeriod} />}
                          </>
                        :
                          <>
                              <div className={styles.cro1381ShowMobile} id="cro1381ShowMobile">
                                <Cro1381SubTotal
                                  id="checkout-amount-details"
                                  className="sideTableHolder"
                                  currency = {currency}
                                  cancellationMessage={cancellationMessage}
                                />
                              </div>
                            {showDiscountForm && <DiscountForm isBlackFridayPeriod={isBlackFridayPeriod} />}
                          </>
                        }
                        {!earliestDepDate &&
                          <DepartureDate
                            register={register}
                            errors={errors}
                            control={control}
                            trigger={trigger}
                            touchedFields={touchedFields}
                          />
                        }
                        {cartIsSuccess &&
                          isCro1381 &&
                          (getLanguageCode(config.brand, true) === "/en" ||
                            getLanguageCode(config.brand, true) === "/en-ie" ||
                            getLanguageCode(config.brand, true) === "/au" ||
                            !getLanguageCode(config.brand, true)) &&
                            (!isChec642 || !earliestDepDate || shouldShowPaymentOptions()) && (
                              <PaymentOptions currency={currency} />
                          )
                        }
                        <CuxInfo
                          register={register}
                          errors={errors}
                          setError={setError}
                          touchedFields={touchedFields}
                          setValue={setValue}
                          getValues={getValues}
                          guestInfo={guestInfo}
                          onGuestInfoChange={handleGuestInfoChange}
                          cartMeta={cartMeta}
                          control={control}
                          clearErrors={clearErrors}
                        />
                        <TicketDetails
                          register={register}
                          control={control}
                          trigger={trigger}
                          errors={errors}
                          touchedFields={touchedFields}
                          setValue={setValue}
                          getValues = {getValues}
                          guestInfo={guestInfo}
                          setGuestInfo={setGuestInfo}
                          isMainGuestChecked={guestInfo.isMainGuestChecked}
                        />
                        <BillingAddress
                          register={register}
                          getCountry = {getCountry}
                          errors={errors}
                          setValue={setValue}
                          control={control}
                          touchedFields={touchedFields}
                          getValues={getValues}
                          resetField={resetField}
                          clearErrors={clearErrors}
                        />
                        {/* {showDeliveryAddressOption && (
                          <DeliveryAddress
                            register={register}
                            errors={errors}
                            setValue={setValue}
                            touchedFields={touchedFields}
                            getValues={getValues}
                            control={control}
                          />
                        )}
                        {!isPostagePaid && showDeliveryAddressOption &&(
                          <DeliveryOptions
                            register={register}
                            errors={errors}
                            control={control}
                            setValue={setValue}
                            touchedFields={touchedFields}
                          />
                        )} */}
                        {cartIsSuccess && !isCro1381 && <PaymentOptions currency = {currency}/>}
                      </React.Fragment>
                    )}

                    <PaymentDetails
                      func={paymentIsValidCheck}
                      selectedCountry = {country}
                      paymentErrorFunc={paymentErrorCheck}
                      showPaymentError={showPaymentError}
                      setShowPaymentError={setShowPaymentError}
                      touchedFields={touchedFields}
                      currency={currency}
                      applePayFailed={applePaymentFailed}
                      showExternalPaymentPayMessage={showExternalPaymentPayMessage}
                      setShowExternalPaymentError={setShowExternalPaymentError}
                      externalPaymentPayMessageRef={externalPaymentPayMessageRef}
                      paymentDetailsTitleRef={paymentDetailsTitleRef}
                      showExternalPaymentError={showExternalPaymentError}
                      isFormValid={window.Cypress ? true : isValid}
                      submittingForm={handleSubmit(onSubmit)}
                      showFormFunc={showFormFunc}
                      shouldLoadPaypal={shouldLoadPaypal}
                      isFormSubmitted={formSubmitted}
                      openModal={openModal}
                      message3DSShowModal={message3DSShowModal}
                      orderCreationStatus={orderCreationStatus}
                      setOrderCreationStatus={setOrderCreationStatus}
                      handleClose3DSModal={handleClose3DSModal}
                      setOrder={setOrder}
                      setOpenModal={setOpenModal}
                    />

                    {((!formSubmitted && config.allowMarketing) || config.brand === "Atd") && (
                      <Marketing
                        register={register}
                        errors={errors}
                        touchedFields={touchedFields}
                        setValue={setValue}
                      />
                    )}

                    {(!formSubmitted || config.brand === "Atd") && (
                      <Terms
                        register={register}
                        errors={errors}
                        touchedFields={touchedFields}
                        resetField={resetField}
                        setValue={setValue}
                        isFormSubmitted={isSubmitted}
                        setError={setError}
                      />
                    )}

                    <div className={styles.success}></div>
                    <input type="hidden" id="submit-order-id" value="" />
                    <div className={styles.submitButtons}>

                     {paymentMethod === "apple-pay" &&
                        <StandardButton
                          className="applePayButton"
                          type="submit"
                          id="submitform"
                          name="submitform"
                          disabled={formSubmitted}
                          loading={formSubmitted}
                        ><apple-pay-button buttonstyle="black" type="plain" id="apple-pay-native"></apple-pay-button></StandardButton>

                      }

                      {paymentMethod === "paypal"  &&
                       <div
                          id="paypalButton"
                          className="paypalButton"
                        />
                      }

                      {paymentMethod === "google-pay" &&
                        <StandardButton
                          className="googlePayButton"
                          type="submit"
                          id="gpay"
                          name="submitform"
                          disabled={formSubmitted}
                          loading={formSubmitted}
                          onClick={() =>{
                            showPayWithExternalPaymentMethodFunc(true);
                            googleDataLayer.createUserClickInputEvent('GooglePayButtonClick');
                          }}
                        />
                      }

                      { ((paymentMethod === "card") || (paymentMethod === "gift-card") || (paymentMethod === "ideal-hosted")) &&
                      <>
                        <StandardButton
                          className="bookNow"
                          type="submit"
                          id="submitform"
                          name="submitform"
                          disabled={formSubmitted}
                          loading={formSubmitted}
                          value="Book now"
                          onClick={() => googleDataLayer.createUserClickInputEvent('bookNowClick')}
                        >
                          {isCro1381 && <LockIcon />}
                          {t('book-now')}
                        </StandardButton>
                        <div className={styles.spinner} />
                        </>
                        }
                    </div>
                  </DepDateContext.Provider>
                </OrderSummaryContext.Provider>
              </OrderContext.Provider>
            </form>
            {(isCro1381 && config.brand === "Atd") &&  (
              <>
                <div className="hideDesktop" data-testid="social-love-section">
                  <SocialLove
                    isHotelCart={isHotelCart}
                  />
                </div>
                  { isFive9Available && (getLanguageCode(config.brand,true) === "/en" || getLanguageCode(config.brand,true) === "/en-ie") &&
                 <div className={styles.socialCall}>
                  <div className={styles.socialCallContent}>
                    <div className={styles.content}>
                      <StandardH4>{t('social-love-chat-heading')}</StandardH4>
                      <p>{t('social-love-chat-content')}</p>
                    </div>
                    <div className={styles.icon}>
                      <LiveChatIcon />
                    </div>
                  </div>
                  <div className={styles.socialCallCTA}>
                    <button className={styles.button} onClick={
                        () => {
                          if (!isChatOpen) {
                            const five9Element = document.getElementById('five9-maximize-button');
                            if (typeof(five9Element) != 'undefined' && five9Element != null) {
                              five9Element.click();
                            }
                            setIsChatOpen(true);
                            googleDataLayer.createUserClickInputEvent('five9ChatClick')
                            return;
                          }
                        }
                      }>
                      <span><SpeechIcon /></span>{t('social-love-chat-button')}
                    </button>
                  </div>
                </div> }
              </>
            )}
          </div>
          <div className={styles.side}>
            {(isCro1381 ?
              <div className={styles.cro1381Sticky}>
                <div className={styles.cro1381Sidebar} ref={sideRef}>
                  {(!cartMeta.is_hotel_cart && config.brand !== "Sw") && (
                    <div className={styles.cro1381BasketHeader}>
                      <a
                        href="#checkout-summary"
                        className={`${styles.cro1381TicketSummaryTrigger} ${showHideHeadingClass}`}
                        onClick={(e) => {
                          e.preventDefault();
                          setShowSummary(!showSummary);
                        }}
                      ><BasketIcon /> {t('Basket')}
                      </a>
                    </div>
                  )}
                  <div className={cartMeta.is_hotel_cart ? styles.cro1381HotelsSidebar : showHideTableClass}>
                    {((config.brand !== "Sw" && cartMeta.is_hotel_cart ))?
                      ""
                    :
                      <>
                        <CartSummary currency = {currency}/>
                      </>
                    }
                    <div className={!cartMeta.is_hotel_cart && styles.cro1381HideMobile}>
                      <OrderSummaryContext.Provider value={orderSummaryValue}>
                        <SubTotal
                          id="checkout-amount-details"
                          className="sideTableHolder"
                          currency = {currency}
                          setCancellationMessage={setCancellationMessage}
                          cancellationMessage={cancellationMessage}
                        />
                      </OrderSummaryContext.Provider>
                    </div>
                  </div>
                </div>
                <div className="hideMobile" data-testid="social-love-section">
                  <SocialLove isHotelCart={isHotelCart}/>
                </div>
              </div>
            :
              <OrderSummaryContext.Provider value={orderSummaryValue}>
                <SubTotal
                  id="checkout-amount-details"
                  className="sideTableHolder"
                  currency = {currency}
                  setCancellationMessage={setCancellationMessage}
                  cancellationMessage={cancellationMessage}
                />
              </OrderSummaryContext.Provider>
            )}
          </div>

        </div>

      </ABContext.Provider>
    </div>
    {(isCro1381 && config.brand === "Atd" ) && (
      <div className={styles.chec407Trustpilot}>
        <TrustBox getLanguageCode={getLanguageCode} />
      </div>
    )}
    </>

  );
};

const sendError = (error) => {
  let errorData = '';
  if (error.response) {
    const responseInfo = {
      status: error.response.status,
      data: error.response.data,
      config:  filterData.filter(error.response.config.data || {})
    };

    errorData = JSON.stringify(responseInfo);
    Logs.AddErrorEvent(errorData);
  }
};

(() =>{
  // Save the original console.error function
  const originalConsoleError = console.error;

  // Decorate console.error
  console.error = function(...args) {
    Logs.AddEvent(`Console Error: ${args}`);

    originalConsoleError.apply(console, args);
  };
})()

export default Form;
